<script setup lang="ts">
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref } from 'vue'
import { prizes, prizeSchedule } from '@/partners/lingo/lingo-giveaway/config/prizes'
import { T } from '@tolgee/vue'
import BaseInput from '@/partners/lingo/lingo-giveaway/components/BaseInput.vue'
import BaseSelect from '@/partners/lingo/lingo-giveaway/components/BaseSelect.vue'
import { countries } from '@/partners/lingo/lingo-giveaway/utils/countries'
import type { WinnerContact } from '@/partners/lingo/lingo-giveaway/types/giveaway'
import { isiOS } from '@/helpers/browser'
import { submitContact } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'

const props = defineProps<{
  wonPrizes: number[]
  contactData: WinnerContact | null
}>()

const emit = defineEmits<{
  (e: 'onSubmit'): void
}>()

const isIOSBrowser = isiOS()

const prizeList = computed(() => props.wonPrizes.map((prize) => prizes[prizeSchedule[prize]]))

const email = ref<string>(props.contactData?.email ?? '')
const country = ref<string>(props.contactData?.country ?? '')
const twitter = ref<string>(props.contactData?.twitter ?? '')
const wallet = ref<string>(props.contactData?.walletAddress ?? '')

const emailErrorMessage = ref<string>('')
const walletErrorMessage = ref<string>('')

const isInputInFocus = ref<boolean>(false)
const isSubmitLoading = ref<boolean>(false)

const interval = ref<ReturnType<typeof setTimeout> | null>(null)

function pushUpModal() {
  if (!isIOSBrowser) return
  const modal = document.querySelector('#base-modal') as HTMLElement | undefined
  if (!modal) return
  modal.style.bottom = '200px'
}

function pushDownModal() {
  if (!isIOSBrowser) return
  const modal = document.querySelector('#base-modal') as HTMLElement | undefined
  if (!modal) return
  modal.style.bottom = '0px'
}

function onFocus() {
  if (interval.value !== null) {
    clearTimeout(interval.value)
  }
  isInputInFocus.value = true
  pushUpModal()
}

function onBlur() {
  if (interval.value !== null) {
    clearTimeout(interval.value)
  }
  pushDownModal()
  interval.value = setTimeout(() => {
    isInputInFocus.value = false
  }, 100)
}

function setCountry(value: { key: string, text: string } | undefined) {
  if (value) {
    country.value = value.key
  }
}

async function onSubmit() {
  isSubmitLoading.value = true
  emailErrorMessage.value = ''
  walletErrorMessage.value = ''
  const response = await submitContact(props.wonPrizes, { walletAddress: wallet.value, country: country.value, email: email.value, twitter: twitter.value })

  if (Object.keys(response).includes('success')) {
    isSubmitLoading.value = false
    setTimeout(() => {
      emit('onSubmit')
      return
    }, 300)
  }
  if (Object.keys(response).includes('errors')) {
    if (response.errors?.email) {
      emailErrorMessage.value = 'This email is invalid'
    }
    if (response.errors?.walletAddress) {
      walletErrorMessage.value = 'This wallet address is invalid'
    }
    isSubmitLoading.value = false
  } else {
    isSubmitLoading.value = false
    emit('onSubmit')
  }
}
</script>
<template>
  <div class="w-full flex flex-1 flex-col items-center gap-y-8 h-full overflow-auto scrollable-container">
    <div class="w-full flex flex-col h-full gap-y-6 justify-center items-center">
      <div class="flex items-center gap-x-6 justify-center w-full">
        <div
          v-for="(prize, index) in prizeList"
          :key="index"
          class="relative size-[120px] min-h-[120px] overflow-hidden rounded-xl"
        >
          <img
            class="relative z-10 size-full"
            :src="prize.src"
            :alt="prize.name"
          >
        </div>
      </div>

      <div class="flex flex-col items-center text-center">
        <h2 class="text-2xl font-bold uppercase -tracking-[0.48px] text-light-white max-[380px]:whitespace-pre-line">
          <T
            key-name="lingo.giveaway.winner_form.title"
            :default-value="`Congratulations! \nyou won!`"
          />
        </h2>
        <p>
          <T
            key-name="lingo.giveaway.winner_form.description"
            default-value="Fill your contact to receive your reward."
          />
        </p>
      </div>
    </div>
    <div class="flex flex-col w-full h-full flex-1 gap-y-3">
      <BaseInput
        :model-value="email"
        placeholder="Email address"
        label="Email address"
        :error-message="emailErrorMessage"
        :has-error="!!emailErrorMessage"
        @update:model-value="(value: string) => email = value"
        @on-focus="onFocus()"
        @on-blur="onBlur"
        @focusout="onBlur"
      />
      <BaseSelect
        :model-value="country ? {key: country, text: countries[country as keyof typeof countries]} : undefined"
        :menu-items="Object.keys(countries).map((key) => { return {key: key, text: countries[key as keyof typeof countries]}})"
        label="Country of residence"
        placeholder="Select..."
        @update:model-value="setCountry"
      />
      <BaseInput
        :model-value="twitter"
        placeholder="X account handle"
        label="X account"
        @update:model-value="(value: string) => twitter = value"
        @on-focus="onFocus()"
        @on-blur="onBlur"
        @focusout="onBlur"
      />
      <BaseInput
        :model-value="wallet"
        placeholder="EVM wallet address"
        label="EVM wallet address"
        hint-message="This can be any Ethereum wallet you own"
        :error-message="walletErrorMessage"
        :has-error="!!walletErrorMessage"
        @update:model-value="(value: string) => wallet = value"
        @on-focus="onFocus()"
        @on-blur="onBlur"
        @focusout="onBlur"
      />
    </div>
    <div
      v-if="isInputInFocus"
      class="h-[240px] w-full"
    />
    <div
      v-if="!isInputInFocus"
      class="flex flex-col gap-y-3 w-full sticky pb-8 bottom-0 z-[10]"
    >
      <BaseButton
        class="w-full"
        style-type="secondary"
        :loading="isSubmitLoading"
        :disabled="!email || !wallet || !country"
        @click="onSubmit"
      >
        <T
          key-name="lingo.giveaway.winner_form.cta"
          default-value="Submit"
        />
      </BaseButton>
    </div>
  </div>
</template>
<style scoped>
.scrollable-container {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

.scrollable-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}
</style>